import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InsightsIcon from '@mui/icons-material/Insights';
import ScienceIcon from '@mui/icons-material/Science';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MapIcon from '@mui/icons-material/Map';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';

import { Page } from '../components';
import { ROUTES } from '../router/constants/router-constants';

export const Home = () => <Page maxWidth={false} noPaper render={() => <Content />} />;

interface RequestCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

const requestCards: RequestCard[] = [
  {
    title: 'Batch Rate Request',
    description: 'Upload shipment data and get carrier rates for multiple shipments at once',
    icon: <RateReviewIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.BATCH_RATE.path,
    color: '#1976d2', // blue
  },
  {
    title: 'AP Testing',
    description: 'Validate carrier rates for auditing specific results',
    icon: <ScienceIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.AP_TESTING.path,
    color: '#d32f2f', // red
  },
  {
    title: 'Rate Compare Request',
    description: 'Compare and analyze detailed rate charges across carriers',
    icon: <CompareArrowsIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.RATE_COMPARE.path,
    color: '#0288d1', // light blue
  },
  {
    title: 'Parcel Rate Request',
    description: 'Upload shipment data and get parcel rates',
    icon: <LocalShippingIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.PARCEL_RATE.path,
    color: '#5e35b1', // deep purple
  },
  {
    title: 'GEO Mileage',
    description: 'Populate missing geographic and mileage data',
    icon: <MapIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.GEO_MILEAGE.path,
    color: '#388e3c', // dark green
  },
  {
    title: 'Invoice Audit Analysis',
    description: 'Analyze manual carrier invoices for shipments by carrier',
    icon: <ReceiptIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.INVOICE_AUDIT.path,
    color: '#e91e63', // pink
  },
  {
    title: 'Custom Analysis',
    description: 'Upload your data file and run a custom analysis request',
    icon: <CloudUploadIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.UPLOAD_ANALYZE.path,
    color: '#f57c00', // orange
  },
  {
    title: 'Shipment Retrieval',
    description: 'Extract shipment data from the system for reporting or analysis',
    icon: <CloudDownloadIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.PULL_DATA.path,
    color: '#7b1fa2', // purple
  },
  {
    title: 'View Results',
    description: 'Browse and download result files from all rating requests',
    icon: <AssessmentIcon sx={{ fontSize: 48 }} />,
    path: ROUTES.RESULTS.path,
    color: '#00695c', // teal
  },
  // {
  //   title: 'Pull & Send Request',
  //   description: 'Pull data from the system and send to any request type',
  //   icon: <InsightsIcon sx={{ fontSize: 48 }} />,
  //   path: ROUTES.PULL_AND_SEND.path,
  //   color: '#c2185b', // pink
  // },
];

const Content = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 0, maxWidth: 1600, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
        Mass Rating & Analysis Home
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        What would you like to do today? Select a request type to get started, or view past results from all request types in one place.
      </Typography>

      <Grid container spacing={3}>
        {requestCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={card.path}>
            <Card
              sx={{
                height: '100%',
                minHeight: 200,
                transition: 'transform 0.2s, box-shadow 0.2s',
                border: '1px solid #e0e0e0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(card.path)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                }}
              >
                <CardContent sx={{ width: '100%', p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      mb: 1,
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                    }}
                  >
                    {card.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
